import { post, put } from '@/utils/client';
import { MEDIA_STATUS, MEDIA_UPLOAD } from '@/lib/routes/apiEndpoints';
import { CropImageType } from '@/types/scheduler/post/media/mediaCropType';

export type TransformationsType =
    | {
          transform: 'crop_resize';
          crop: Omit<CropImageType, 'crop_type'>;
      }
    | {
          transform: 'convert';
          convert: string;
      }
    | {
          transform: 'transcode';
          transcode: 'default';
      }
    | {
          transform: 'crop_resize';
          resize: {
              width: number;
              height: number;
          };
      };

type FileUrlType = { file: File } | { url: string };

type MediaType = {
    media_type: string;
};

type ReturnValue = {
    data: { media_id: string } & Partial<TransformationsType>;
};

type UploadMediaParamsType = {
    dataToUpload: Partial<TransformationsType> & FileUrlType & MediaType;
    cancelToken?: AbortController;
};

type DataToSendType = MergeUnion<TransformationsType> & MergeUnion<FileUrlType> & MediaType;

export async function uploadMedia({
    dataToUpload,
    cancelToken
}: UploadMediaParamsType): Promise<ReturnValue['data']> {
    try {
        const dataToSend = dataToUpload as DataToSendType;
        const formData = new FormData();
        Object.keys(dataToSend).forEach((key) => {
            const value = dataToSend[key];
            if (value instanceof File) {
                return formData.append(key, value);
            } else if (typeof value === 'object') {
                return Object.entries(value).forEach(([keyItem, valueItem]) => {
                    return formData.append(`${key}[${keyItem}]`, valueItem.toString());
                });
            }
            formData.append(key, value);
        });
        const response = await post<ReturnValue>(MEDIA_UPLOAD, formData, {
            signal: cancelToken?.signal
        });
        if (!response) {
            return;
        }
        return response.data;
    } catch (error) {
        throw new Error(`errors:error_occured_submit_data: ${error}`);
    }
}

type UpdateMediaParamsType = {
    media_id: string;
    params: {
        media_type?: string;
    } & TransformationsType;
    cancelToken?: AbortController;
};

export async function updateMedia({
    media_id,
    params,
    cancelToken
}: UpdateMediaParamsType): Promise<ReturnValue['data']> {
    try {
        const response = await put<ReturnValue>(`${MEDIA_STATUS}/${media_id}`, params, {
            signal: cancelToken?.signal
        });
        if (!response) {
            return;
        }
        return response.data;
    } catch (error) {
        throw new Error();
    }
}
